import {
  type TagReadable,
  leadsListQueryKey,
  leadsPipelineListQueryKey,
  tagsCreateMutation,
  tagsDestroyMutation,
  tagsListListQueryKey,
  tagsListOptions,
  tagsListQueryKey
} from '@crm/api'
import { defaultNonPagination } from '@crm/utils'
import { FormColorPicker } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ModalProps,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Tag
} from 'antd'
import { useTheme } from 'antd-style'
import { CloseCircle } from 'iconsax-react'

type Props = { onSuccess?: (tag: TagReadable) => void } & ModalProps

export const AddEditTag = ({ onSuccess, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { invalidateQueries: invalidateQueriesHook } =
    useInvalidatePredicateQueries()

  const { data, isLoading } = useQuery({
    ...tagsListOptions({
      query: {
        ...defaultNonPagination
      }
    })
  })

  const invalidateQueries = () => {
    invalidateQueriesHook([
      tagsListQueryKey(),
      leadsPipelineListQueryKey(),
      leadsListQueryKey(),
      tagsListListQueryKey()
    ])
    queryClient.removeQueries({ queryKey: tagsListQueryKey() })
    queryClient.removeQueries({ queryKey: tagsListListQueryKey() })
  }

  const { mutate: createMutate, isPending: isCreatePending } = useMutation({
    ...tagsCreateMutation(),
    onSuccess: (data) => {
      invalidateQueries()
      form.resetFields(['name'])
      onSuccess?.(data)
    },
    onError: (error) => showApiErrors(error, form)
  })

  const { mutate: deleteMutate } = useMutation({
    ...tagsDestroyMutation(),
    onSuccess: () => invalidateQueries(),
    onError: (error) => showApiErrors(error, form)
  })

  const onCreate = () => {
    const values = form.getFieldsValue()

    createMutate({
      body: { ...values, color: values?.color?.toHexString?.() || values.color }
    })
  }

  return (
    <Modal
      title={t('nomenclature.tags')}
      loading={isLoading}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      okButtonProps={{ loading: isCreatePending }}
      onOk={form.submit}
      {...props}
    >
      <Flex gap={8} vertical>
        <Flex gap={8} wrap>
          {data?.results?.map(({ color, name, id }) => (
            <Flex align="center" key={id}>
              <Tag color={color}>{name}</Tag>

              <Popconfirm
                title={t('confirm.are_you_sure_description')}
                onConfirm={() => id && deleteMutate({ path: { id } })}
              >
                <CloseCircle style={{ cursor: 'pointer' }} size={14} />
              </Popconfirm>
            </Flex>
          ))}
        </Flex>

        <Form
          form={form}
          layout="vertical"
          onFinish={onCreate}
          initialValues={{ color: theme.colorPrimary }}
        >
          <Row gutter={8}>
            <Col span={16}>
              <Form.Item
                name="name"
                label={t('nomenclature.name')}
                rules={[{ required: true }]}
              >
                <Input placeholder={t('nomenclature.name')} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Flex justify="end">
                <FormColorPicker
                  itemProps={{
                    label: t('nomenclature.color'),
                    rules: [{ required: true }]
                  }}
                  showText
                />
              </Flex>
            </Col>
          </Row>
        </Form>
      </Flex>
    </Modal>
  )
}
